import React, { useState } from "react"
import Fuse from "fuse.js"
import "bootstrap/dist/css/bootstrap.min.css"
import { FormattedMessage, useIntl } from "react-intl"
import Table from "react-bootstrap/Table"
import DegreeLevelIndicator from "../majors/DegreeLevelIndicator"
import {
  WingsButton,
  WingsHeading,
  WingsLink,
  WingsSearchBar,
  WingsText,
} from "../../global-styles/wingspans-ui/components"
import "./schools-majors-degrees.css"

const INITIAL_VISIBLE_MAJORS_COUNT = 6
const MAJORS_EXPAND_STEP = 20

const insertWbrsAtSlashes = (str) => {
  if (!str) {
    return []
  }
  const chunks = str.split("/")
  const output = [chunks[0]]
  for (const chunk of chunks.slice(1)) {
    output.push(<wbr/>, "/", chunk)
  }
  return output
}

const MajorRow = ({
  majorName="",
  majorNameEs="",
  cipCode="",
  programTitle="",
  programURL="",
  hasCertificate=false,
  hasAssociates=false,
  hasBachelors=false,
  hasGraduate=false,
  showCertificate=true,
  showAssociates=true,
  showBachelors=true,
  showGraduate=true,
  showProgramLink=true,
  rowClassName="",
  rowStyle=undefined,
}) => {
  const { locale } = useIntl()

  return (
    <tr className={rowClassName} style={rowStyle}>
      <td>
        <WingsLink to={`/major-cip/${cipCode}/`} size="h6">
          {insertWbrsAtSlashes((locale === "es-ES") ? majorNameEs : majorName)}
        </WingsLink>
      </td>
      {showCertificate && (
        <td className="degree-level-cell">
          <DegreeLevelIndicator degreeLevel="certificate" isHighlighted={hasCertificate} />
        </td>
      )}
      {showAssociates && (
        <td className="degree-level-cell">
          <DegreeLevelIndicator degreeLevel="associates" isHighlighted={hasAssociates} />
        </td>
      )}
      {showBachelors && (
        <td className="degree-level-cell">
          <DegreeLevelIndicator degreeLevel="bachelors" isHighlighted={hasBachelors} />
        </td>
      )}
      {showGraduate && (
        <td className="degree-level-cell">
          <DegreeLevelIndicator degreeLevel="graduate" isHighlighted={hasGraduate} />
        </td>
      )}
      {showProgramLink && (
        <td>
          {programURL && (
            <WingsLink
              to={programURL}
              size="body"
              className="w-text-right"
              display="block"
              isExternal
              isNewTab
            >
              {programTitle || majorName}
            </WingsLink>
          )}
        </td>
      )}
    </tr>
  )
}

const SectionMajorsDegrees = ({ programData=[] }) => {
  const { locale, formatMessage } = useIntl()
  const [visibleMajorsCount, setVisibleMajorsCount] = useState(INITIAL_VISIBLE_MAJORS_COUNT)
  const [expandStartIndex, setExpandStartIndex] = useState(null)

  const hasMajors = programData ?? false

  const fuse =
    hasMajors &&
    new Fuse(programData, {
      keys: ["cip_title", "cip_title_es", "program_title"],
      includeScore: true,
      minMatchCharLength: 2,
    })

  const [fuseQuery, setFuseQuery] = useState("")
  const results = hasMajors && fuse.search(fuseQuery)
  const fuseQueryResults = hasMajors ? results.map((result) => result.item) : []

  let cipCategories = new Set()
  if (programData) {
    programData.forEach((program) => {
      if (!cipCategories.has(program.group_id)) {
        cipCategories.add((locale === "es-ES") ? program.group_name_es : program.group_name)
      }
    })
  }
  const sortedCipCategories = [...cipCategories].sort()

  const sortByLocalizedMajorName = (majorA, majorB) => {
    const propToSortBy = (locale === "es-ES") ? "cip_title_es" : "cip_title"
    return majorA[propToSortBy].localeCompare(majorB[propToSortBy])
  }
  const orderedMajors = fuseQuery ? fuseQueryResults : (
    sortedCipCategories.map(category => {
      const matchingPrograms = programData.filter(p => category === p[(locale === "es-ES") ? "group_name_es" : "group_name"])
                                          .sort(sortByLocalizedMajorName)
      return [category, ...matchingPrograms]
    }).flat()
  )
  const displayedMajors = fuseQuery ? orderedMajors : orderedMajors.slice(0, visibleMajorsCount)
  const schoolHasProgramLinks = programData.some(p => p.program_url)

  const handleViewMore = () => {
    setExpandStartIndex(visibleMajorsCount)
    setVisibleMajorsCount((currentCount) => currentCount + MAJORS_EXPAND_STEP)
  }

  // Check which degree level columns have data
  const hasCertificatePrograms = programData.some(p => p.certificate)
  const hasAssociatesPrograms = programData.some(p => p.associates)
  const hasBachelorsPrograms = programData.some(p => p.bachelors)
  const hasGraduatePrograms = programData.some(p => p.graduate)

  // Calculate colspan for degree levels header
  const degreeColumnsCount = [hasCertificatePrograms, hasAssociatesPrograms, hasBachelorsPrograms, hasGraduatePrograms].filter(Boolean).length

  return (
    <>
      {programData && (
        <>
        <div className="w-flex w-justify-end">
          <WingsSearchBar
            className="school-search-header"
            placeholder={formatMessage({ id: "start-typing-major", defaultMessage: "Start typing to find a desired major" })}
            value={fuseQuery}
            onChange={setFuseQuery}
          />
        </div>

          <Table responsive="lg" className="majors-table">
              <thead>
                <tr>
                  <th width="431px">
                    <WingsHeading level={5} className="w-mb-1">
                      <FormattedMessage id="degree-area" defaultMessage="Degree Area" />
                    </WingsHeading>
                  </th>
                  {degreeColumnsCount > 0 && (
                    <th width={`${degreeColumnsCount * 144}px`} colSpan={degreeColumnsCount}>
                      <WingsHeading level={5} className="w-mb-1">
                        <FormattedMessage id="level-of-degree-offered" defaultMessage="Level of Degree Offered" />
                      </WingsHeading>
                    </th>
                  )}
                  {schoolHasProgramLinks && (
                    <th width="175px" className="w-text-right">
                      <WingsHeading level={5} className="w-mb-1">
                        <FormattedMessage id="program-link" defaultMessage="Program Link" />
                      </WingsHeading>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="paragraph">
                {displayedMajors.map((major, majorIndex) => {
                  const shouldAnimateExpand = !fuseQuery && expandStartIndex !== null && majorIndex >= expandStartIndex
                  const expandRowClassName = shouldAnimateExpand ? "major-row-expand-enter" : ""
                  const expandRowStyle = shouldAnimateExpand ? { "--expand-row-index": majorIndex - expandStartIndex } : undefined

                  return (typeof major === "string") ? (
                    <tr key={major} className={`major-group-row ${expandRowClassName}`.trim()} style={expandRowStyle}>
                      <td colSpan={1 + degreeColumnsCount + (schoolHasProgramLinks ? 1 : 0)} className="major-group-cell">
                        <WingsText className="w-font-bold">{major}</WingsText>
                      </td>
                    </tr>
                  ) : (
                    <MajorRow
                      majorName={major.cip_title}
                      majorNameEs={major.cip_title_es}
                      cipCode={major.cip_code}
                      programTitle={major.program_title}
                      programURL={major.program_url}
                      hasCertificate={major.certificate}
                      hasAssociates={major.associates}
                      hasBachelors={major.bachelors}
                      hasGraduate={major.graduate}
                      showCertificate={hasCertificatePrograms}
                      showAssociates={hasAssociatesPrograms}
                      showBachelors={hasBachelorsPrograms}
                      showGraduate={hasGraduatePrograms}
                      showProgramLink={schoolHasProgramLinks}
                      rowClassName={expandRowClassName}
                      rowStyle={expandRowStyle}
                      key={major.cip_code}
                    />
                  )
                })}
              </tbody>
          </Table>

          {!fuseQuery && orderedMajors.length > visibleMajorsCount && (
            <div className={`view-more white w-flex w-justify-center`}>
              <div className={`view-more__inner w-flex w-justify-center`}>
                <WingsButton
                  variant="tertiary"
                  size="medium"
                  onClick={handleViewMore}
                >
                  <span>
                    <FormattedMessage id="view-more" defaultMessage="View More" />
                  </span>
                  <span className={`icon--chevron-toggle`}>
                    <span className={`chevron-down`}></span>
                  </span>
                </WingsButton>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default SectionMajorsDegrees
